import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { relativeTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

const severityColor = (sev) => {
  switch (sev) {
    case "danger":  return "#E74C3C";
    case "warning": return "#F39C12";
    default:        return "#2ECC71";
  }
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const containerRef = useRef(null);
  const pollTimer = useRef(null);

  const refreshCount = async () => {
    try {
      const { data } = await apiClient.get("/notifications/count");
      setCount(data.unread_count || 0);
    } catch {
      /* ignore */
    }
  };

  const refreshList = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/notifications?limit=20");
      setItems(data.notifications || []);
      setCount(data.unread_count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Poll count every 30 s
  useEffect(() => {
    refreshCount();
    pollTimer.current = setInterval(refreshCount, 30_000);
    return () => clearInterval(pollTimer.current);
  }, []);

  // Load list when opening
  useEffect(() => {
    if (open) refreshList();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const markOne = async (id) => {
    try {
      await apiClient.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
      setCount((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await apiClient.post("/notifications/read-all");
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || now })));
      setCount(0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openItem = async (n) => {
    if (!n.read_at) await markOne(n.id);
    setOpen(false);
    if (n.debtor_id) nav(`/debtors/${n.debtor_id}`);
  };

  const showBadge = count > 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="relative p-2 rounded-md text-text-secondary hover:text-accent-pink hover:bg-bg-tertiary transition-colors"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        data-testid="header-notifications"
      >
        <Bell size={18} />
        {showBadge && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center animate-pulse-alert"
            data-testid="header-notifications-badge"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-2 w-[360px] max-h-[480px] overflow-hidden bg-bg-secondary border border-border-default rounded-[12px] shadow-elevated z-[80] flex flex-col"
            data-testid="notifications-dropdown"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-default">
              <div className="text-white font-semibold text-[14px]">Notifications</div>
              {count > 0 && (
                <button
                  type="button"
                  onClick={markAll}
                  className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold transition-colors"
                  data-testid="notifications-mark-all-read"
                >
                  Mark All Read
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-text-secondary text-[13px]" data-testid="notifications-loading">
                  Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="p-8 flex flex-col items-center text-center gap-2" data-testid="notifications-empty">
                  <Bell size={28} className="text-text-muted" />
                  <div className="text-text-secondary text-[14px]">No notifications</div>
                </div>
              ) : (
                items.map((n) => {
                  const unread = !n.read_at;
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => openItem(n)}
                      className={cn(
                        "w-full text-left px-5 py-4 border-b border-border-default hover:brightness-125 transition-all",
                        unread ? "bg-bg-tertiary" : "bg-bg-secondary"
                      )}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                          style={{ background: severityColor(n.severity) }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <div
                              className={cn(
                                "text-[14px] truncate",
                                unread ? "text-white font-semibold" : "text-text-secondary"
                              )}
                            >
                              {n.title}
                            </div>
                            <div className="text-[11px] text-text-muted shrink-0">{relativeTime(n.created_at)}</div>
                          </div>
                          {n.body && (
                            <div className="text-[12px] text-text-secondary mt-0.5">{n.body}</div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Re-export the help icon here for AuthShell to compose next to it.
export const HeaderHelpIcon = () => (
  <button
    type="button"
    className="p-2 rounded-md text-text-secondary hover:text-accent-pink hover:bg-bg-tertiary transition-colors"
    aria-label="Help"
    data-testid="header-help"
  >
    <HelpCircle size={18} />
  </button>
);

export default NotificationBell;
